import React, {
   Dispatch,
   PropsWithChildren,
   ReactElement,
   SetStateAction, useCallback,
   useEffect, useMemo,
   useState
} from 'react';
import AntdMenu from 'antd/lib/menu';
import AntdBadge from 'antd/lib/badge';
import classnames from 'classnames';

/**
 * {@docCategory Menu}
 */
export interface MenuItemProps {
   /**
    * Unique ID of the menu item
    */
   key: string;

   /**
    * Set display title for collapsed item
    */
   title: string;

   /**
    * The icon of the menu item
    */
   icon?: ReactElement;

   /**
    * Whether menu item is disabled
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * The badge to display on the menu.
    */
   badge?: string | number;

   /**
    * Called when the user clicks the menu item.
    * @param param - The click param
    */
   onClick?: (param: ClickParam) => void;

   /**
    * SubMenuItemProps are exactly the same as MenuItemProps, except Icon is optional
    */
   menuItems?: MenuItemProps[];

   /**
    * An optional class name to add to the menu item
    */
   className?: string;
}

/**
 * {@docCategory Menu}
 */
export interface ClickParam {
   /**
    * The key of the menu item clicked
    */
   key: string;

   /**
    * For use with sub menu items, this will return the keys of the parents also
    */
   keyPath: Array<string>;

   item: unknown;

   domEvent: React.MouseEvent<HTMLElement>;
}

export type RenderIconType = React.ReactNode | ((props: any) => React.ReactNode);

/**
 * {@docCategory Menu}
 */
export interface MenuProps {
   menuItems: MenuItemProps[];

   /**
    * The key of the currently selected menu item
    */
   selectedKey?: string;

   /**
    * Specifies the collapsed status for inline mode
    */
   collapsed?: boolean;

   /**
    * Type of menu
    * @defaultvalue 'inline'
    */
   mode?: 'inline' | 'vertical' | 'horizontal';

   /**
    * Allows selecting menu items
    * @defaultvalue true
    */
   selectable?: boolean;

   /**
    * Color theme of the menu
    * @defaultvalue light
    */
   theme?: 'light' | 'dark';

   /**
    * Called when a menu item is clicked
    */
   onClick?: (param: ClickParam) => void;

   /**
    * Array with the keys of currently opened sub-menus
    */
   openKeys?: string[];

   /**
    * Called when sub-menus are opened or closed
    * @param openKeys
    */
   onOpenChange?: (openKeys: React.Key[]) => void;

   /**
    * An optional class name to add to the menu
    */
   className?: string;

   /**
    * Optional prefixCls
    */
   prefixCls?: string;

   /**
    * Custom expand icon of submenu
    */
   expandIcon?: RenderIconType;
}

type OpenKeysType = React.Key[] | {
   key: React.Key;
   item: React.ReactInstance;
   trigger: string;
   open: boolean;
};

export const Menu = (props: PropsWithChildren<MenuProps>) => {
   const [selectedKey, setSelectedKey] = useState(props.selectedKey);

   useEffect(() => {
      setSelectedKey(props.selectedKey);
   }, [props.selectedKey]);

   const selectedKeyArray = useMemo(()=>(
      selectedKey ? [selectedKey] : []
   ),[selectedKey]);

   const menuHasIcons = props.menuItems.map((menuItem) => menuItem.icon !== undefined).reduce((prevValue, newValue) => (prevValue || newValue));

   const inlineCollapsedProps = {
      inlineCollapsed: props.collapsed
   };

   return (
      <AntdMenu
         mode={props.mode || 'inline'}
         selectable={props.selectable}
         theme={props.theme}
         selectedKeys={selectedKeyArray}
         onOpenChange={props.onOpenChange}
         openKeys={props.openKeys}
         className={classnames('uf-menu', { 'uf-menu-collapsed': props.collapsed }, props.className)}
         prefixCls={props.prefixCls}
         expandIcon={props.expandIcon}
         {...inlineCollapsedProps}
      >
         {props.menuItems.map((item) => {
            return createMenuItem(props, item, props.openKeys || [], setSelectedKey, menuHasIcons);
         })}
      </AntdMenu>
   );
};

const createMenuItem = (props: MenuProps,
                        item: MenuItemProps,
                        openKeys: string[],
                        setSelectedKey: Dispatch<SetStateAction<string | undefined>>,
                        menuHasIcons: boolean) => {
   if (item.menuItems) {

      const subMenuHasIcons = item.menuItems.map((menuItem) => menuItem.icon !== undefined).reduce((prevValue, newValue) => (prevValue || newValue));

      return (
         <AntdMenu.SubMenu
            rootPrefixCls={props.prefixCls}
            key={item.key}
            title={menuItemContent(props, item, menuHasIcons)}
            disabled={item.disabled}
            className={'uf-menu-submenu'}
         >
            {item.menuItems.map((subItem) => {
               return createMenuItem(props, subItem as MenuItemProps, openKeys, setSelectedKey, subMenuHasIcons);
            })}
         </AntdMenu.SubMenu>
      );
   }

   const className = classnames('uf-menu-item', item.className);

   return <AntdMenu.Item
      rootPrefixCls={props.prefixCls}
      key={item.key}
      disabled={item.disabled}
      onClick={proxyOnClick(props, setSelectedKey, item.onClick)}
      className={className}
   >
      {menuItemContent(props, item, menuHasIcons)}
   </AntdMenu.Item>;
};

interface MenuInfo {
   key: React.Key;
   keyPath: React.Key[];
   item: React.ReactInstance;
   domEvent: React.MouseEvent<HTMLElement>;
}

const proxyOnClick = (props: MenuProps,
                      setSelectedItem: Dispatch<SetStateAction<string | undefined>>,
                      onClick?: (param: ClickParam) => void) => {
   return (param: MenuInfo) => {
      setSelectedItem("" + param.key);

      const convertedParam = {
         key: "" + param.key,
         keyPath: param.keyPath.map(key => "" + key),
         item: param.item,
         domEvent: param.domEvent
      };

      if (props.onClick) {
         props.onClick(convertedParam);
      }

      if (onClick) {
         onClick(convertedParam);
      }
   };
};

const menuItemContent = (props: MenuProps, item: MenuItemProps, menuHasIcons: boolean) => {

   if (item.badge === undefined) {
      return <>
         { menuHasIcons ? <span className={"anticon"}>{item.icon}</span> : null }
         <span>{item.title}</span>
      </>;
   }
   else {
      if (props.collapsed && props.menuItems.indexOf(item) >= 0) {
         return (<>
            {menuHasIcons ?
                <span className={"anticon"}>
                  <AntdBadge size={"small"}
                             className={'uf-menu-text-badge collapsed'}
                             count={item.badge}
                             offset={[14, 4]}>
                     {item.icon}
                  </AntdBadge>
               </span>
                          :
                null
            }
            <span>{item.title}</span>
         </>);
      }

      return <>
         {menuHasIcons ?
            <span className={"anticon"}>{item.icon}</span>
          :
            null
         }
         <span>{item.title}</span>
         <AntdBadge className={'uf-menu-text-badge expanded'} size={"small"} count={item.badge}/>
      </>;
   }
};
